import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Icon from "../components/Icon";
import { Bluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../hooks/useConfirm";
import CommunityIntegrationCard from "./CommunityIntegrationCard";
import ConnectCommunityIntegrationModal from "./ConnectCommunityIntegrationModal";
import CreateCommunityIntegrationModal from "./CreateCommunityIntegrationModal";

export default function CommunityIntegrations() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth();
    const [confirm, ConfirmDialog] = useConfirm();
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [connectTarget, setConnectTarget] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const fetchIntegrations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await Bluelagoon.getCommunityIntegrations();
            const data = res?.data?.data || res?.data;
            setIntegrations(data?.integrations || []);
        } catch { setIntegrations([]); }
        setLoading(false);
    }, []);

    useEffect(() => { fetchIntegrations(); }, [fetchIntegrations]);

    const filtered = integrations.filter((i) =>
        !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.baseUrl || "").toLowerCase().includes(search.toLowerCase())
    );

    const handleEdit = (integration) => navigate(`/integrate/build/${integration.id}`);

    const handleDisconnect = async (integration) => {
        try {
            await confirm({
                title: t("communityIntegrations.confirmDisconnectTitle", { defaultValue: "Disconnect integration?" }),
                description: t("communityIntegrations.confirmDisconnect", { name: integration.name }),
                confirmText: t("integrations.community.disconnect"),
                cancelText: t("common.cancel"),
                isDestructive: true,
            });
        } catch { return; }
        try {
            const res = await Bluelagoon.disconnectCommunityIntegration({ communityIntegrationId: integration.id });
            const body = res?.data?.data || res?.data;
            if (res?.status === 200 || body?.success) {
                toast.success(t("communityIntegrations.disconnected", { name: integration.name, defaultValue: "Disconnected from {{name}}." }));
            } else {
                toast.error(body?.message || t("common.somethingWentWrong"));
            }
        } catch (err) {
            toast.error(err?.response?.data?.data?.message || err?.message || t("common.somethingWentWrong"));
        } finally {
            fetchIntegrations();
        }
    };

    const handleDelete = async (integration) => {
        try {
            await confirm({
                title: t("communityIntegrations.confirmDeleteTitle", { defaultValue: "Delete integration?" }),
                description: t("communityIntegrations.confirmDelete", { name: integration.name }),
                confirmText: t("common.delete"),
                cancelText: t("common.cancel"),
                isDestructive: true,
            });
        } catch { return; }
        try {
            const res = await Bluelagoon.deleteCommunityIntegration({ integrationId: integration.id });
            const body = res?.data?.data || res?.data;
            if (res?.status === 200 || body?.success) {
                toast.success(t("communityIntegrations.deleted", { name: integration.name, defaultValue: "Deleted {{name}}." }));
            } else {
                toast.error(body?.message || t("common.somethingWentWrong"));
            }
        } catch (err) {
            toast.error(err?.response?.data?.data?.message || err?.message || t("common.somethingWentWrong"));
        } finally {
            fetchIntegrations();
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
                <div className="relative flex-1 max-w-sm">
                    <Icon name="Search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" placeholder={t("communityIntegrations.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 border rounded-md text-sm" />
                </div>
                <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium shrink-0">
                    <Icon name="Plus" className="w-4 h-4" />
                    {t("communityIntegrations.createButton")}
                </button>
            </div>
            {loading ? (
                <div className="flex items-center justify-center py-16 text-gray-400"><div className="w-6 h-6 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" /></div>
            ) : filtered.length === 0 ? (
                <EmptyState hasSearch={!!search} onCreate={() => setShowCreateModal(true)} />
            ) : (
                <>
                    <p className="text-sm text-gray-500">{t("communityIntegrations.integrationCount", { count: filtered.length })}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map((i) => <CommunityIntegrationCard key={i.id} integration={i} currentUserId={user?.id} onEdit={handleEdit} onConnect={setConnectTarget} onDisconnect={handleDisconnect} onDelete={handleDelete} />)}
                    </div>
                </>
            )}
            {connectTarget && (
                <ConnectCommunityIntegrationModal integration={connectTarget} onClose={() => setConnectTarget(null)} onConnected={() => { setConnectTarget(null); fetchIntegrations(); }} />
            )}
            <ConfirmDialog />
            {showCreateModal && (
                <CreateCommunityIntegrationModal
                    onClose={() => setShowCreateModal(false)}
                    onStart={(visibility) => { setShowCreateModal(false); navigate("/integrate/build", { state: { visibility } }); }}
                />
            )}
        </div>
    );
}

function EmptyState({ hasSearch, onCreate }) {
    const { t } = useTranslation();
    return (
        <div className="text-center py-16">
            <Icon name={hasSearch ? "Search" : "Puzzle"} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">{hasSearch ? t("communityIntegrations.noSearchResults") : t("communityIntegrations.noIntegrations")}</p>
            {!hasSearch && (
                <button onClick={onCreate} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">
                    {t("communityIntegrations.createFirst")}
                </button>
            )}
        </div>
    );
}
